import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { useSEO } from '@/hooks/useSEO';

// ... existing code ...

const schemaData = {
  '@context': 'https://schema.org',
  '@type': 'CollectionPage',
  '@id': `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/blog#webpage`,
  url: `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/blog`,
  name: 'ACHC Accreditation & Compliance Blog | Advantixx',
  description: 'Expert articles, guides, and research-backed strategies to help your home health agency achieve and maintain ACHC accreditation with confidence.',
  isPartOf: { '@id': `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/#website` },
  about: {
    '@type': 'Thing',
    name: 'ACHC Accreditation and Home Health Compliance',
  },
  publisher: {
    '@type': 'Organization',
    name: 'Advantixx Home Health Accreditation Consultants',
    url: import.meta.env.VITE_SITE_URL || 'https://example.com',
  },
};

export default function BlogPage() {
  useSEO({
    title: 'ACHC Accreditation & Compliance Blog | Advantixx',
    description: 'Expert articles, guides, and research-backed strategies to help your home health agency achieve and maintain ACHC accreditation with confidence.',
    keywords: 'ACHC accreditation blog, home health compliance articles, accreditation tips, ACHC survey preparation, home health agency guide',
    canonical: '/blog',
  });

  useEffect(() => {
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'schema-blog';
    script.text = JSON.stringify(schemaData);
    document.head.appendChild(script);
    return () => {
      const el = document.getElementById('schema-blog');
      if (el) el.remove();
    };
  }, []);

  return (
    <div className="min-h-screen bg-[#0d0d0d] font-['Montserrat',sans-serif]">
      <Navbar />

      {/* Hero */}
      <section className="relative pt-40 pb-24 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/10d32a0434eb31a9357300e88787d4f7.jpg"
            alt="Blog background"
            className="w-full h-full object-cover object-top opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0d0d0d]/60 via-[#0d0d0d]/80 to-[#0d0d0d]"></div>
        </div>
        <div className="relative max-w-7xl mx-auto px-6 md:px-10 text-center">
          <span className="inline-block text-[#d4af37] text-xs uppercase tracking-[4px] font-semibold mb-5">
            Knowledge Hub
          </span>
          <h1 className="text-4xl md:text-6xl font-black text-white leading-tight mb-6">
            ACHC Accreditation &amp;<br />
            <span className="text-[#d4af37]">Compliance Insights</span>
          </h1>
          <p className="text-[#aaa] text-lg max-w-2xl mx-auto leading-relaxed">
            Expert articles, guides, and research-backed strategies to help your home health agency achieve and maintain ACHC accreditation with confidence.
          </p>
        </div>
      </section>

      {/* Category Filter */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 mb-14">
        <div className="flex flex-wrap gap-3 justify-center">
          {['All', 'Accreditation', 'Compliance', 'Survey Prep', 'Operations', 'Technology', 'Policy'].map((cat) => (
            <button
              key={cat}
              className={`whitespace-nowrap px-5 py-2 rounded-full text-sm font-medium transition-all duration-200 cursor-pointer ${
                cat === 'All'
                  ? 'bg-[#d4af37] text-[#0d0d0d]'
                  : 'border border-[#d4af37]/30 text-[#888] hover:border-[#d4af37] hover:text-[#d4af37]'
              }`}
            >
              {cat}
            </button>
          ))}
        </div>
      </section>

      {/* Featured Post */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 mb-16">
        <div className="relative rounded-2xl overflow-hidden group cursor-pointer">
          <img
            src="https://readdy.ai/api/search-image?query=professional%20healthcare%20consultant%20reviewing%20compliance%20documents%20in%20a%20modern%20office%20with%20gold%20accents%2C%20clean%20minimalist%20workspace%2C%20warm%20lighting%2C%20high%20quality&width=800&height=500&seq=blog1&orientation=landscape"
            alt="The Complete Guide to ACHC Accreditation for Home Health Agencies"
            className="w-full h-[480px] object-cover object-top group-hover:scale-105 transition-transform duration-700"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0d0d0d] via-[#0d0d0d]/60 to-transparent"></div>
          <div className="absolute bottom-0 left-0 right-0 p-8 md:p-12">
            <div className="flex items-center gap-4 mb-4">
              <span className="bg-[#d4af37] text-[#0d0d0d] text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wide">Featured</span>
              <span className="text-[#d4af37] text-xs font-medium">Accreditation</span>
              <span className="text-[#666] text-xs">March 15, 2026</span>
              <span className="text-[#666] text-xs">8 min read</span>
            </div>
            <h2 className="text-2xl md:text-4xl font-black text-white mb-4 max-w-3xl leading-tight">
              The Complete Guide to ACHC Accreditation for Home Health Agencies
            </h2>
            <p className="text-[#aaa] text-base max-w-2xl mb-6 leading-relaxed">
              Everything your home health agency needs to know about achieving ACHC accreditation — from initial readiness assessments to final survey preparation and beyond.
            </p>
            <Link
              to="/blog/achc-accreditation-guide"
              className="inline-flex items-center gap-2 bg-[#d4af37] text-[#0d0d0d] font-bold text-sm px-6 py-3 rounded-full hover:bg-[#f4c430] transition-colors duration-200 whitespace-nowrap cursor-pointer"
            >
              Read Full Article
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </div>
      </section>

      {/* Blog Grid */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 mb-24">
        <div className="flex items-center justify-between mb-10">
          <h2 className="text-2xl font-black text-white">Latest Articles</h2>
          <span className="text-[#555] text-sm">9 articles</span>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
          {[
            {
              slug: 'home-health-compliance-tips',
              category: 'Compliance',
              date: 'February 28, 2026',
              readTime: '6 min read',
              title: 'Top 10 Compliance Tips Every Home Health Agency Must Know in 2026',
              excerpt: 'Staying compliant in the home health industry is more critical than ever. Discover the top compliance strategies that protect your agency from costly penalties and survey failures.',
              image: 'https://readdy.ai/api/search-image?query=healthcare%20compliance%20checklist%20on%20a%20desk%20with%20a%20stethoscope%20and%20pen%2C%20dark%20elegant%20background%20with%20gold%20tones%2C%20professional%20medical%20office%20setting&width=800&height=500&seq=blog2&orientation=landscape',
              tags: ['Compliance', 'Regulations'],
            },
            {
              slug: 'mock-survey-preparation',
              category: 'Survey Prep',
              date: 'January 20, 2026',
              readTime: '7 min read',
              title: 'How to Prepare for a Mock Survey: A Step-by-Step Approach',
              excerpt: 'Mock surveys are one of the most powerful tools for ensuring your agency is ready for the real thing. Learn how to conduct an effective mock survey and what to do with the results.',
              image: 'https://readdy.ai/api/search-image?query=healthcare%20team%20in%20a%20meeting%20room%20reviewing%20survey%20preparation%20documents%2C%20professional%20setting%20with%20dark%20walls%20and%20warm%20gold%20lighting%2C%20organized%20and%20focused&width=800&height=500&seq=blog3&orientation=landscape',
              tags: ['Mock Survey', 'Survey Prep'],
            },
            {
              slug: 'achc-vs-joint-commission',
              category: 'Comparison',
              date: 'February 10, 2026',
              readTime: '10 min read',
              title: 'ACHC vs. The Joint Commission: Which Accreditation Is Right for Your Agency?',
              excerpt: 'A detailed comparison of ACHC and The Joint Commission — costs, timelines, standards complexity, and which is the better fit for your home health agency.',
              image: 'https://readdy.ai/api/search-image?query=two%20healthcare%20professionals%20comparing%20documents%20side%20by%20side%20at%20a%20modern%20conference%20table%2C%20professional%20healthcare%20compliance%20setting%2C%20warm%20gold%20lighting&width=800&height=500&seq=blogcomp1&orientation=landscape',
              tags: ['ACHC', 'Comparison'],
            },
            {
              slug: 'home-health-cms-conditions-of-participation',
              category: 'Compliance',
              date: 'January 20, 2026',
              readTime: '12 min read',
              title: 'Understanding CMS Conditions of Participation for Home Health Agencies',
              excerpt: 'A comprehensive breakdown of CMS CoPs — what they are, why they matter, and how ACHC accreditation satisfies them so you do not need a separate state survey.',
              image: 'https://readdy.ai/api/search-image?query=healthcare%20compliance%20professional%20studying%20federal%20regulations%20at%20a%20clean%20modern%20desk%2C%20professional%20medical%20office%2C%20warm%20ambient%20lighting%2C%20gold%20accents&width=800&height=500&seq=blogcms1&orientation=landscape',
              tags: ['CMS', 'CoPs'],
            },
            {
              slug: 'qapi-program-home-health',
              category: 'Quality',
              date: 'January 5, 2026',
              readTime: '9 min read',
              title: 'Building a Compliant QAPI Program for Your Home Health Agency',
              excerpt: 'A robust QAPI program is non-negotiable for ACHC accreditation. Learn the 5 core elements, key metrics to track, and common deficiencies to avoid.',
              image: 'https://readdy.ai/api/search-image?query=healthcare%20quality%20improvement%20team%20reviewing%20performance%20metrics%20on%20a%20large%20monitor%20in%20a%20modern%20medical%20office%2C%20warm%20gold%20lighting%2C%20clean%20minimalist%20design&width=800&height=500&seq=blogqapi1&orientation=landscape',
              tags: ['QAPI', 'Quality'],
            },
            {
              slug: 'home-health-policy-procedure-development',
              category: 'Policy',
              date: 'December 15, 2025',
              readTime: '11 min read',
              title: 'Policy & Procedure Development for Home Health ACHC Accreditation',
              excerpt: 'Your P&P manual is the backbone of ACHC compliance. Learn what categories are required, best practices for development, and how to ensure policy matches practice.',
              image: 'https://readdy.ai/api/search-image?query=healthcare%20administrator%20writing%20and%20organizing%20policy%20documents%20in%20a%20professional%20medical%20office%2C%20organized%20binders%20on%20a%20clean%20desk%2C%20warm%20gold%20lighting&width=800&height=500&seq=blogpp1&orientation=landscape',
              tags: ['Policy', 'Procedures'],
            },
            {
              slug: 'home-health-staff-training-achc',
              category: 'Staff Training',
              date: 'November 18, 2025',
              readTime: '8 min read',
              title: 'Staff Training Requirements for ACHC Accreditation: A Complete Guide',
              excerpt: 'Staff training is one of the most scrutinized areas during an ACHC survey. Learn what to train, how to document it, and how to prepare your team for surveyor interviews.',
              image: 'https://readdy.ai/api/search-image?query=home%20health%20agency%20staff%20attending%20a%20professional%20compliance%20training%20session%20in%20a%20bright%20modern%20conference%20room%2C%20engaged%20healthcare%20workers%20taking%20notes%2C%20warm%20gold%20lighting&width=800&height=500&seq=blogst1&orientation=landscape',
              tags: ['Training', 'Staff'],
            },
            {
              slug: 'starting-home-health-agency',
              category: 'Startup Guide',
              date: 'October 22, 2025',
              readTime: '14 min read',
              title: 'How to Start a Home Health Agency and Get ACHC Accredited',
              excerpt: 'A step-by-step guide to starting a home health agency from scratch — licensing, Medicare certification, ACHC accreditation, and everything in between.',
              image: 'https://readdy.ai/api/search-image?query=entrepreneur%20and%20healthcare%20professional%20planning%20a%20new%20home%20health%20agency%20business%20with%20documents%20and%20laptop%20in%20a%20modern%20bright%20office%2C%20warm%20gold%20lighting&width=800&height=500&seq=blogstart1&orientation=landscape',
              tags: ['Startup', 'Licensing'],
            },
            {
              slug: 'achc-accreditation-guide',
              category: 'Operations',
              date: 'December 10, 2025',
              readTime: '5 min read',
              title: 'Building a Compliance-First Culture in Your Home Health Agency',
              excerpt: 'Compliance is not just a checklist — it is a culture. Learn how to embed regulatory awareness into your agency daily operations and staff mindset for lasting success.',
              image: 'https://readdy.ai/api/search-image?query=diverse%20healthcare%20team%20collaborating%20in%20a%20bright%20modern%20office%2C%20professional%20and%20motivated%2C%20warm%20tones%20with%20gold%20accents%2C%20teamwork%20and%20leadership&width=800&height=500&seq=blog4&orientation=landscape',
              tags: ['Culture', 'Leadership'],
            },
          ].map((post, i) => (
            <article
              key={i}
              className="bg-[#111] rounded-xl overflow-hidden border border-[#1e1e1e] hover:border-[#d4af37]/30 transition-all duration-300 group cursor-pointer"
            >
              <div className="overflow-hidden h-52">
                <img
                  src={post.image}
                  alt={post.title}
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="p-6">
                <div className="flex items-center gap-3 mb-3">
                  <span className="text-[#d4af37] text-xs font-semibold uppercase tracking-wide">{post.category}</span>
                  <span className="text-[#444] text-xs">•</span>
                  <span className="text-[#555] text-xs">{post.readTime}</span>
                </div>
                <h3 className="text-white font-bold text-base leading-snug mb-3 group-hover:text-[#d4af37] transition-colors duration-200">
                  {post.title}
                </h3>
                <p className="text-[#666] text-sm leading-relaxed mb-5 line-clamp-3">{post.excerpt}</p>
                <div className="flex flex-wrap gap-2 mb-5">
                  {post.tags.map((tag) => (
                    <span key={tag} className="text-[#555] text-xs border border-[#2a2a2a] px-2.5 py-1 rounded-full">{tag}</span>
                  ))}
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-[#444] text-xs">{post.date}</span>
                  <Link
                    to={`/blog/${post.slug}`}
                    className="flex items-center gap-1.5 text-[#d4af37] text-xs font-semibold hover:gap-2.5 transition-all duration-200 whitespace-nowrap cursor-pointer"
                  >
                    Read More <i className="ri-arrow-right-line"></i>
                  </Link>
                </div>
              </div>
            </article>
          ))}
        </div>
      </section>

      {/* Newsletter CTA */}
      <section className="bg-[#111] border-t border-b border-[#1e1e1e] py-20 mb-0">
        <div className="max-w-3xl mx-auto px-6 md:px-10 text-center">
          <div className="w-14 h-14 flex items-center justify-center mx-auto mb-6 rounded-full bg-[#d4af37]/10 border border-[#d4af37]/30">
            <i className="ri-mail-send-line text-[#d4af37] text-2xl"></i>
          </div>
          <h2 className="text-3xl font-black text-white mb-4">
            Get Expert ACHC Insights Delivered to You
          </h2>
          <p className="text-[#888] mb-8 leading-relaxed">
            Join hundreds of home health agency leaders who receive our compliance tips, accreditation updates, and industry insights every month.
          </p>
          <form
            data-readdy-form
            onSubmit={async (e) => {
              e.preventDefault();
              const form = e.currentTarget as HTMLFormElement;
              const data = new URLSearchParams(new FormData(form) as unknown as Record<string, string>);
              await fetch('https://readdy.ai/api/form/d7cmdmkkpdh0a4advlng', {
                method: 'POST',
                headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                body: data.toString(),
              });
              form.reset();
            }}
            className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto"
          >
            <input
              type="email"
              name="email"
              placeholder="Your email address"
              required
              className="flex-1 bg-[#0d0d0d] border border-[#2a2a2a] text-white text-sm px-5 py-3 rounded-full outline-none focus:border-[#d4af37]/50 placeholder-[#444]"
            />
            <button
              type="submit"
              className="whitespace-nowrap bg-[#d4af37] text-[#0d0d0d] font-bold text-sm px-7 py-3 rounded-full hover:bg-[#f4c430] transition-colors duration-200 cursor-pointer"
            >
              Subscribe Free
            </button>
          </form>
        </div>
      </section>

      <Footer />
    </div>
  );
}
